import { HubConnectionBuilder } from "@microsoft/signalr"
import { get, forEach, map, uniqueId } from "lodash"
import { API_SERVER_NOTIFICATIONS, API_SERVER_NOTIFICATIONS_RECEIVE_ONLY } from "../constants/api"
import ActionTypes from "../enums/ActionTypes"
import { preparePrefixUrl } from "../utils/api"

const CONFLICTS_COUNT = "conflictsCountAsync"
const PLAN_ON_OPERATION_IDS = "SendPlanOnOperationIdsAsync"
const PLAN_OFF_OPERATION_IDS = "SendPlanOffOperationIdsAsync"
const SERVER_TO_CLIENT_MSG = "serverToClientMsgAsync"
const CLIENT_TO_SERVER_MSG = "clientToServerMsgAsync"
const sockets = {}

const getSocketToken = (store, action) => {
  if (action.type === ActionTypes.USER_LOGIN_SUCCESS || action.type === ActionTypes.PIN_LOGIN_SUCCESS) {
    return get(action, "payload.user.token")
  }
  if (action.type === ActionTypes.PIN_AUTO_LOGIN) {
    return get(store.getState(), "user.terminalAuthentication.token")
  }
  return get(action, "payload.token")
}

const fallbackStart = socket => {
  setTimeout(() => {
    if (socket && socket.state === "Disconnected") {
      socket.start().catch(() => {
        fallbackStart(socket)
      })
    }
  }, 3000)
}

const createSocket = (store, api, token) => {
  const socket = new HubConnectionBuilder()
    // .configureLogging(LogLevel.Debug)
    .withUrl(api, {
      accessTokenFactory: () => token,
    })
    .build()
  socket.start().then(() => initializeHandlers(socket, store))
  socket.onclose(err => {
    const actualToken =
      get(store.getState(), "user.authentication.token", null) ||
      get(store.getState(), "user.terminalAuthentication.token", null)
    if (err) {
      // eslint-disable-next-line no-console
      console.log("Connection closed with error: ", err)
    }
    if (err || actualToken) {
      fallbackStart(socket)
    }
  })
  return socket
}

const initializeHandlers = (socket, store) => {
  socket.on(CONFLICTS_COUNT, message => {
    store.dispatch({
      type: ActionTypes.SOCKET_RECEIVED_CONFLICTS_COUNT,
      payload: message,
    })
  })
  socket.invoke(CONFLICTS_COUNT, null)

  socket.on(PLAN_ON_OPERATION_IDS, operationIds => {
    store.dispatch({
      type: ActionTypes.SOCKET_RECEIVED_PLAN_ON_OPERATION_IDS,
      payload: operationIds,
    })
  })

  socket.on(PLAN_OFF_OPERATION_IDS, operationIds => {
    store.dispatch({
      type: ActionTypes.SOCKET_RECEIVED_PLAN_OFF_OPERATION_IDS,
      payload: operationIds,
    })
  })

  // incoming messages
  socket.on(SERVER_TO_CLIENT_MSG, () => {
    const tempId = uniqueId()
    store.dispatch({
      type: ActionTypes.SOCKET_RECEIVED_MESSAGE,
      payload: { tempId },
    })
  })
}

// outgoing messages
const handleOutgoing = (socket, action) => {
  if (socket && action.type === ActionTypes.SEND_WEBSOCKET_MESSAGE) {
    const { receiverUserIds } = action.payload
    socket.send(CLIENT_TO_SERVER_MSG, receiverUserIds)
  }
}

const createSocketsMiddleware = store => {
  // Sometimes we will have cases when we have multiple instances working with one database (i.e BMW).
  // In order to be able to receive messages from all instances, we keep a connection for each additional api, but only for listening.
  const apis = [API_SERVER_NOTIFICATIONS, ...(API_SERVER_NOTIFICATIONS_RECEIVE_ONLY || [])]
  const {
    simulation: { id: simulationId },
  } = store.getState()
  const apisWithPrefix = map(apis, api => preparePrefixUrl(api, simulationId))
  return next => action => {
    if (
      action.type === ActionTypes.USER_AUTO_LOGIN_SUCCESS ||
      action.type === ActionTypes.USER_AUTO_LOGIN_TERMINAL_SUCCESS ||
      action.type === ActionTypes.USER_LOGIN_SUCCESS ||
      action.type === ActionTypes.PIN_AUTO_LOGIN ||
      action.type === ActionTypes.PIN_LOGIN_SUCCESS
    ) {
      const token = getSocketToken(store, action)
      forEach(apisWithPrefix, api => {
        const socket = sockets[api]
        if (!socket || socket.state === "Disconnected") {
          sockets[api] = createSocket(store, api, token)
        }
      })
    }

    if (
      action.type === ActionTypes.USER_AUTO_LOGIN_FAILURE ||
      action.type === ActionTypes.USER_AUTO_LOGIN_TERMINAL_FAILURE ||
      action.type === ActionTypes.LOGOUT_TO_TERMINAL_REQUEST ||
      action.type === ActionTypes.USER_LOGOUT_REQUEST
    ) {
      forEach(apisWithPrefix, api => {
        const socket = sockets[api]
        if (socket && socket.state !== "Disconnected") {
          socket.stop()
        }
      })
    }

    handleOutgoing(sockets[preparePrefixUrl(API_SERVER_NOTIFICATIONS, simulationId)], action)

    return next(action)
  }
}

export default createSocketsMiddleware
